import type { CSSProperties } from 'react';
import { X, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

type SelectedFilterChipSize = 'default' | 'sm';

type TintedChipStyle = CSSProperties & Record<'--chip-color', string>;

const PRIMARY_CHIP_COLOR_CLASS = '[--chip-color:var(--color-primary)]';

const CHIP_CLASS = cn(
  'flex w-fit max-w-full items-center rounded-full bg-card/80',
  'ring-1 ring-[color-mix(in_oklab,var(--chip-color)_25%,transparent)]',
  'shadow-[0_4px_12px_-8px_color-mix(in_oklab,var(--chip-color)_60%,transparent)]',
);

const ICON_CIRCLE_CLASS =
  'flex shrink-0 items-center justify-center rounded-full bg-[color-mix(in_oklab,var(--chip-color)_12%,transparent)] text-(--chip-color)';

const CLEAR_BUTTON_CLASS =
  'flex shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-[color-mix(in_oklab,var(--chip-color)_12%,transparent)] hover:text-(--chip-color)';

const CHIP_SIZE_CLASS: Record<SelectedFilterChipSize, string> = {
  default: 'gap-2 py-1 pr-1 pl-1 text-sm',
  sm: 'gap-1.5 py-0.5 pr-0.5 pl-0.5 text-xs',
};

const ICON_CIRCLE_SIZE_CLASS: Record<SelectedFilterChipSize, string> = {
  default: 'size-6',
  sm: 'size-5.5',
};

const ICON_SIZE_CLASS: Record<SelectedFilterChipSize, string> = {
  default: 'size-3.5',
  sm: 'size-3',
};

const LABEL_SIZE_CLASS: Record<SelectedFilterChipSize, string> = {
  default: 'text-xs',
  sm: 'text-2xs',
};

const CLEAR_BUTTON_SIZE_CLASS: Record<SelectedFilterChipSize, string> = {
  default: 'size-7 max-sm:size-8',
  sm: 'size-5 max-sm:size-7',
};

const CLEAR_ICON_SIZE_CLASS: Record<SelectedFilterChipSize, string> = {
  default: 'size-4',
  sm: 'size-3',
};

interface SelectedFilterChipProps {
  icon: LucideIcon;
  label: string;
  value: string;
  clearLabel: string;
  onClear: () => void;
  size?: SelectedFilterChipSize;
  tintColor?: string | undefined;
}

export function SelectedFilterChip({
  icon: Icon,
  label,
  value,
  clearLabel,
  onClear,
  size = 'default',
  tintColor,
}: SelectedFilterChipProps) {
  const tintedStyle: TintedChipStyle | undefined = tintColor
    ? { '--chip-color': tintColor }
    : undefined;

  return (
    <div
      style={tintedStyle}
      className={cn(CHIP_CLASS, !tintColor && PRIMARY_CHIP_COLOR_CLASS, CHIP_SIZE_CLASS[size])}
    >
      <span className={cn(ICON_CIRCLE_CLASS, ICON_CIRCLE_SIZE_CLASS[size])}>
        <Icon className={ICON_SIZE_CLASS[size]} aria-hidden />
      </span>
      <span className="min-w-0 truncate">
        <span className={cn('text-muted-foreground', LABEL_SIZE_CLASS[size])}>{label} </span>
        <span className="font-semibold text-foreground">{value}</span>
      </span>
      <button
        type="button"
        onClick={onClear}
        aria-label={clearLabel}
        title={clearLabel}
        className={cn(CLEAR_BUTTON_CLASS, CLEAR_BUTTON_SIZE_CLASS[size])}
      >
        <X className={CLEAR_ICON_SIZE_CLASS[size]} aria-hidden />
      </button>
    </div>
  );
}
